import { useCallback, useEffect, useRef, useState } from 'react';

import { apiGetPlatformToken } from '../onboarding-api';
import logger from '../utils/logger.ts';
import { returnToCustomerHub } from '../utils/navigation';
import { appendPlatformToken } from '../utils/platformUrl';

type Params = {
  embedded: boolean;
  isMobile: boolean;
  csrfToken: string;
  platformLink: string;
  appOneLink: string;
  userEmail?: string;
  userPassword?: string;
};

export const usePlatformAutoLogin = ({
  embedded,
  isMobile,
  csrfToken,
  platformLink,
  userEmail,
  userPassword,
}: Params) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [platformToken, setPlatformToken] = useState<string | undefined>();

  useEffect(() => {
    if (embedded || isMobile || !csrfToken) {
      return;
    }

    let active = true;

    apiGetPlatformToken(csrfToken).then(({ data, errorCode, errorMessage }) => {
      if (!active) {
        return;
      }

      if (errorCode || !data?.platformToken) {
        logger.error(
          'Failed to pre-load platform token: ' +
            JSON.stringify({
              errorCode,
              errorMessage,
            })
        );
        return;
      }

      setPlatformToken(data.platformToken);
    });

    return () => {
      active = false;
    };
  }, [embedded, isMobile, csrfToken]);

  const postLoginMessage = useCallback(
    (token: string | undefined) => {
      const message = { token, email: userEmail, password: userPassword };
      const target = window.parent !== window ? window.parent : window;
      target.postMessage(message, '*');
    },
    [userEmail, userPassword]
  );

  const onSubmit = useCallback(async () => {
    if (submittingRef.current) {
      return;
    }

    if (embedded) {
      return returnToCustomerHub(embedded);
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    if (isMobile) {
      if (!csrfToken) {
        postLoginMessage(undefined);
        submittingRef.current = false;
        setIsSubmitting(false);
        return;
      }

      let token = platformToken;
      if (!token) {
        const { data, errorCode, errorMessage } = await apiGetPlatformToken(csrfToken);
        if (errorCode || !data?.platformToken) {
          logger.error(
            'Failed to get platform token for login URL: ' +
              JSON.stringify({
                errorCode,
                errorMessage,
              })
          );
          postLoginMessage(undefined);
          submittingRef.current = false;
          setIsSubmitting(false);
          return;
        }

        token = data.platformToken;
      }

      postLoginMessage(token);
      return;
    }

    if (!csrfToken) {
      window.location.href = platformLink;
      return;
    }

    let token = platformToken;
    if (!token) {
      const { data, errorCode, errorMessage } = await apiGetPlatformToken(csrfToken);
      if (errorCode || !data?.platformToken) {
        logger.error(
          'Failed to get platform token for login URL: ' +
            JSON.stringify({
              errorCode,
              errorMessage,
            })
        );
        window.location.href = platformLink;
        return;
      }

      token = data.platformToken;
    }

    window.location.href = appendPlatformToken(platformLink, token);
  }, [embedded, isMobile, csrfToken, platformLink, platformToken, postLoginMessage]);

  return {
    isSubmitting,
    onSubmit,
  };
};
